import React, { useRef, useEffect, useState, useCallback } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-markers/dist/videojs.markers.css";
import "videojs-markers";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { isStarted_GetFetchVideoContent, success_GetFetchVideoContent, error_GetFetchVideoContent } from "../../../redux/reducers/videoSlice";
// import styles from "../create-feedback/CreateFeedback.module.scss"; // Removed unused SCSS
import "./VideojsPlayer.scss";
import AnnotationStage from "./Annotations";

// Ensure videojs-markers plugin is loaded
if (typeof window !== 'undefined' && window.videojs) {
  try {
    require('videojs-markers');
    // Register the plugin globally
    if (window.videojs && !window.videojs.getPlugin('markers')) {
      window.videojs.registerPlugin('markers', require('videojs-markers'));
    }
  } catch (e) {
    console.warn('videojs-markers plugin could not be loaded:', e);
  }
}

// Register custom Video.js buttons only once
if (!videojs.getComponent('SeekBackward')) {
  const Button = videojs.getComponent('Button');
  class SeekBackward extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText("Seek Backward 5s");
      this.addClass('vjs-seek-backward');
      // Double-triangle rewind icon, 20x20, centered
      this.el().innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
          <polygon points="17,4 9,10 17,16" fill="white" stroke="white"/>
          <polygon points="11,4 3,10 11,16" fill="white" stroke="white"/>
        </svg>
      `;
    }
    handleClick() {
      const video = this.player();
      video.currentTime(Math.max(0, video.currentTime() - 5));
    }
  }
  videojs.registerComponent('SeekBackward', SeekBackward);
}
if (!videojs.getComponent('SeekForward')) {
  const Button = videojs.getComponent('Button');
  class SeekForward extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText("Seek Forward 5s");
      this.addClass('vjs-seek-forward');
      // Double-triangle fast-forward icon, 20x20, centered
      this.el().innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" stroke-width="2" xmlns="http://www.w3.org/2000/svg">
          <polygon points="3,4 11,10 3,16" fill="white" stroke="white"/>
          <polygon points="9,4 17,10 9,16" fill="white" stroke="white"/>
        </svg>
      `;
    }
    handleClick() {
      const video = this.player();
      video.currentTime(Math.min(video.duration(), video.currentTime() + 5));
    }
  }
  videojs.registerComponent('SeekForward', SeekForward);
}
if (!videojs.getComponent('ScreenshotButton')) {
  const Button = videojs.getComponent('Button');
  class ScreenshotButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText("Screenshot");
      this.addClass('vjs-screenshot');
      // Extra-large, blocky, bold camera SVG (64x64, thick stroke, filled)
      this.el().innerHTML = `
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="16" width="56" height="40" rx="8" fill="white" stroke="black" stroke-width="5"/>
          <circle cx="32" cy="36" r="15" fill="black" stroke="black" stroke-width="5"/>
          <rect x="18" y="4" width="28" height="14" rx="4" fill="white" stroke="black" stroke-width="5"/>
        </svg>
      `;
    }
    handleClick() {
      const player = this.player();
      const videoEl = player.el().querySelector('video');
      if (!videoEl) return;
      
      try {
        const canvas = document.createElement("canvas");
        canvas.width = videoEl.videoWidth || 640;
        canvas.height = videoEl.videoHeight || 360;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
        const image = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = image;
        link.download = `screenshot-${Date.now()}.png`;
        link.click();
      } catch (err) {
        console.error("Screenshot failed:", err);
        toast.error("Failed to take screenshot. This might be due to security restrictions on the video source.");
      }
    }
  }
  videojs.registerComponent('ScreenshotButton', ScreenshotButton);
}
// ModesButton removed as per user request

// Register custom control bar components
if (!videojs.getComponent('ControlBarLeft')) {
  const Component = videojs.getComponent('Component');
  class ControlBarLeft extends Component {
    constructor(player, options) {
      super(player, options);
      this.addClass('vjs-control-bar-left');
    }
  }
  videojs.registerComponent('ControlBarLeft', ControlBarLeft);
}

if (!videojs.getComponent('ControlBarRight')) {
  const Component = videojs.getComponent('Component');
  class ControlBarRight extends Component {
    constructor(player, options) {
      super(player, options);
      this.addClass('vjs-control-bar-right');
    }
  }
  videojs.registerComponent('ControlBarRight', ControlBarRight);
}

// Suppress VideoJS deprecation warnings
const originalWarn = console.warn;
console.warn = (...args) => {
  const message = args[0];
  if (message && typeof message === 'string') {
    // Suppress VideoJS deprecation warnings
    if (message.includes('videojs.plugin() is deprecated') ||
      message.includes('videojs.mergeOptions is deprecated') ||
      message.includes('videojs.createEl() is deprecated') ||
      message.includes('VIDEOJS: WARN:')) {
      return; // Suppress these specific warnings
    }
  }
  originalWarn.apply(console, args);
};

const VideoPlayer = ({ videoId, env, currentTime, videoLoaded, videoContent, annotations, setAnnotations, setDisplayDialogBoxForNewAIFeedbacks, setLoading, setVideoLoaded, setVideoKey, setFrameRate, setCurrentTime, setVideoDimensions: parentSetVideoDimensions, thumbnailUrl, playerRef, videoRef, setEditingCommentId, handlePauseVideo, handlePlayVideo, handleVideoEnd, standAloneFeedbacks, setEditedCommentText, frameRate, isPaused, addStandaloneFeedback, getCoachFeedback, videoKey, isPlayerSide, showAnnotations }) => {
  const [isPlayerActive, setIsPlayerActive] = useState(false);
  const [annotationsLoaded, setAnnotationsLoaded] = useState(false);

  useEffect(() => { }, []);

  const dispatch = useDispatch();
  const jwtToken = localStorage.getItem("BQS_TOKEN");


  const playerMarkers = (annotations) => {
    if (!playerRef.current) {
      console.error("Player instance is not available.");
      return;
    }

    // Check if markers plugin is available
    if (typeof playerRef.current.markers !== 'function') {
      // Markers plugin not available - skip initialization
      // Try to initialize markers manually if plugin is not available
      if (playerRef.current.markers && typeof playerRef.current.markers === 'object') { } else {
        return;
      }
    }

    // Ensure player is ready
    if (!playerRef.current.readyState || playerRef.current.readyState() < 1) {
      console.warn("Player not ready yet, waiting...");
      setTimeout(() => playerMarkers(annotations), 100);
      return;
    }

    // Create marker objects with proper styling
    const markersToAdd = annotations
      .filter((annotation) => {
        // Filter out invalid annotations
        if (!annotation || !annotation.timeRange || typeof annotation.timeRange.startTime !== 'number') {
          console.warn("Invalid annotation found:", annotation);
          return false;
        }
        return true;
      })
      .map((annotation) => {
        const commentText = annotation?.comments?.[0]?.body?.startsWith("data:audio")
          ? "Audio"
          : annotation?.comments?.[0]?.body || "Annotation";

        // Check if this is a "correct" annotation
        const isCorrect =
          commentText.startsWith("Overall Result - Correct") ||
          commentText.startsWith("Result - Correct");

        return {
          time: annotation.timeRange.startTime, // Directly use start time from annotation
          text: commentText, // Default text if no comment exists
          class: isCorrect ? "vjs-marker-correct" : "vjs-marker-incorrect" // Apply CSS class based on content
        };
      });

    // Add custom CSS for the marker classes only once
    const existingStyle = document.getElementById('marker-styles');
    if (!existingStyle) {
      const style = document.createElement("style");
      style.id = 'marker-styles';
      style.textContent = `
        .vjs-marker-correct {
          background-color: green !important;
        }
        .vjs-marker-incorrect {
          background-color: red !important;
        }
      `;
      document.head.appendChild(style);
    }

    try {
      // Double-check markers is available before using it
      if (typeof playerRef.current.markers !== 'function') {
        // Markers function not available - skip updates
        return;
      }

      // Clear existing markers first
      playerRef.current.markers({
        markers: [],
        markerTip: {
          display: true,
          text: (marker) => marker.text,
        },
        markerStyle: {
          width: "5px",
          height: "20px",
          cursor: "pointer",
          borderRadius: "1px",
          bottom: "-8px",
        },
        onMarkerClick: (marker) => {
          if (playerRef.current) {
            playerRef.current.currentTime(marker.time);
            playerRef.current.play();
          }
        },
        onMarkerReached: (marker) => {
          const time = playerRef.current.currentTime();
          // Pause the video when marker is reached
          if (playerRef.current && !playerRef.current.paused()) {
            playerRef.current.pause();
          }
        },
      });

      // Then add new markers
      playerRef.current.markers({
        markers: markersToAdd,
        markerTip: {
          display: true,
          text: (marker) => marker.text, // Show annotation text on hover
        },
        markerStyle: {
          width: "5px",
          height: "20px",
          cursor: "pointer",
          borderRadius: "1px",
          bottom: "-8px",
        },
        onMarkerClick: (marker) => {
          if (playerRef.current) {
            playerRef.current.currentTime(marker.time);
            playerRef.current.play();
          }
        },
        onMarkerReached: (marker) => {
          const time = playerRef.current.currentTime();
        },
      });
    } catch (error) {
      // Silently handle marker errors - markers are optional
    }
  };
  const updateDimensions = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const containerWidth = video.offsetWidth;
      const containerHeight = video.offsetHeight;

      const videoAspectRatio = video.videoWidth / video.videoHeight;
      const containerAspectRatio = containerWidth / containerHeight;

      let displayWidth, displayHeight;
      let top = 0;
      let left = 0;

      if (containerAspectRatio > videoAspectRatio) {
        // Container is wider than the video aspect ratio
        displayHeight = containerHeight;
        displayWidth = videoAspectRatio * displayHeight;

        // Center the video horizontally
        left = (containerWidth - displayWidth) / 2;
      } else {
        // Container is taller than the video aspect ratio
        displayWidth = containerWidth;
        displayHeight = displayWidth / videoAspectRatio;

        // Center the video vertically
        top = (containerHeight - displayHeight) / 2;
      }

      setVideoDimensions && setVideoDimensions({
        width: displayWidth,
        height: displayHeight,
        top: top,
        left: left,
      });
    }
  };

  const loadVideo = useCallback(() => {
    if (videoRef.current && !playerRef.current && videoLoaded && annotationsLoaded) {
      try {
        const videoUrl =
          // Prefer an explicit URL if provided
          (videoContent && videoContent.videoContentUrl) ? videoContent.videoContentUrl
            : (env === "standalone" ? "/video_js.mp4" : videoContent?.videoContentUrl);
        const fileType = videoUrl.split(".").pop().toLowerCase();
        const mimeType = fileType === "mov" ? "video/quicktime" : "video/mp4";
        const controlBarChildren = isPlayerSide
          ? [
            'SeekBackward',
            'playToggle',
            'SeekForward',
            'volumePanel',
            'playbackRateMenuButton',
            'progressControl',
            'ScreenshotButton',
            'KeyboardHelpButton',
            'PictureInPictureToggle',
            'fullscreenToggle',
          ]
          : [
            'SeekBackward',
            'playToggle',
            'SeekForward',
            'volumePanel',
            'playbackRateMenuButton',
            'progressControl',
            'ScreenshotButton',
            'KeyboardHelpButton',
            'PictureInPictureToggle',
            'fullscreenToggle',
          ];
        const player = videojs(videoRef.current, {
          controls: true,
          preload: "auto",
          playbackRates: [0.1, 0.2, 0.5, 0.75, 1, 2, 3, 5],
          poster: thumbnailUrl,
          sources: [{ src: videoUrl, type: mimeType }],
          crossorigin: 'anonymous',
          controlBar: {
            children: controlBarChildren
          },
          userActions: {
            doubleClick: false,
            hotkeys: false,
          },
          responsive: true,
          fill: true,
          fluid: false, // Ensure fluid is false so fill works
          inactivityTimeout: 0,
          playsinline: true,
        });

        playerRef.current = player;

        // Removed runtime forcing of menu positioning to minimize changes; CSS governs dropdown behavior

        // Initialize markers immediately after player is created (first load)
        if (annotations?.length > 0 && typeof player.markers === 'function') {
          const markersToAdd = annotations.map(annotation => {
            const commentText = annotation?.comments?.[0]?.body?.startsWith("data:audio")
              ? "Audio"
              : annotation?.comments?.[0]?.body || "Annotation";
            const isCorrect =
              commentText.startsWith("Overall Result - Correct") ||
              commentText.startsWith("Result - Correct");
            return {
              time: annotation.timeRange.startTime,
              text: commentText,
              class: isCorrect ? "vjs-marker-correct" : "vjs-marker-incorrect"
            };
          });
          player.markers({
            markers: markersToAdd,
            markerTip: {
              display: true,
              text: marker => marker.text,
            },
            markerStyle: {
              width: "5px",
              height: "20px",
              cursor: "pointer",
              borderRadius: "1px",
              bottom: "-8px",
            },
            onMarkerClick: marker => { },
            onMarkerReached: marker => {
              const time = player.currentTime();
              // Pause the video when marker is reached
              if (player && !player.paused()) {
                player.pause();
              }
            },
          });
        }
        player.on("loadedmetadata", () => {

          // Force start at 0 to avoid browsers resuming at a non-zero offset on large files
          try {
            if (typeof player.currentTime === 'function') {
              player.currentTime(0);
            }
          } catch (e) {
            console.warn('Unable to set currentTime(0) on loadedmetadata:', e);
          }

          if (annotations?.length > 0) {
            playerMarkers(annotations);
          } else {
            playerMarkers([]);
          }
          const videoWidth = player.videoWidth();
          const videoHeight = player.videoHeight();
          const videoRatio = videoWidth / videoHeight;
          if (typeof setFrameRate === 'function') {
            setFrameRate(videoRatio);
          }
          // Attempt to detect frame rate (if metadata or API supports it)
          let detectedFps = 30; // Default to 30 FPS

          try {
            const videoTrack = player?.tech_?.el()?.videoTracks?.[0];
            if (videoTrack && videoTrack.frameRate) {
              detectedFps = videoTrack.frameRate; // Use detected frame rate
            } else if (typeof setFrameRate === 'function') {
              setFrameRate(detectedFps);
            }
          } catch (error) {
            console.warn(
              "Failed to detect frame rate. Using default 30 FPS.",
              error
            );
          }
        });

        player.on("timeupdate", () => {
          if (playerRef.current) {
            const currentTime = playerRef.current.currentTime();
            setCurrentTime(currentTime);
          }
        });

        player.on("play", () => { });
        player.on("pause", () => { });

        player.on("canplay", () => {
          setLoading(false);
        });

        // Add custom volume panel behavior
        const volumePanel = player.controlBar.getChild('volumePanel');
        if (volumePanel) {
          const volumeControl = volumePanel.getChild('volumeControl');
          const volumeButton = volumePanel.getChild('volumeButton');

          if (volumeControl && volumeButton) {
            // Expand volume panel on button click
            volumeButton.on('click', () => {
              volumePanel.addClass('vjs-hover');
            });

            // Collapse when clicking outside or losing focus
            const handleVolumePanelBlur = () => {
              setTimeout(() => {
                if (!volumePanel.hasClass('vjs-hover')) {
                  volumePanel.removeClass('vjs-hover');
                }
              }, 100);
            };

            volumeControl.on('blur', handleVolumePanelBlur);
            volumePanel.on('mouseleave', () => {
              volumePanel.removeClass('vjs-hover');
            });

            // Keep expanded while dragging
            volumeControl.on('mousedown', () => {
              volumePanel.addClass('vjs-hover');
            });

            volumeControl.on('mouseup', () => {
              // Keep expanded for a moment after release
              setTimeout(() => {
                if (!volumePanel.hasClass('vjs-hover')) {
                  volumePanel.removeClass('vjs-hover');
                }
              }, 500);
            });
          }
        }

        window.addEventListener("resize", updateDimensions);
        updateDimensions();

        player.on("error", () => {
          const error = player.error();
          let errorMessage = "An unknown error occurred.";
          switch (error.code) {
            case 2:
              errorMessage = "Network error: Unable to load the video.";
              break;
            case 4:
              errorMessage =
                fileType === "mov"
                  ? "Unsupported format: .mov files may not be supported in this browser."
                  : "Unsupported format: The video type is not compatible.";
              break;
          }
          console.error(errorMessage);
          toast.error(errorMessage);
        });
      } catch (initializationError) {
        console.error(
          "Error initializing Video.js player:",
          initializationError
        );
        toast.error(
          "Failed to initialize the video player. Please contact support."
        );
      }
    } else {
      console.warn(
        "Video reference is not available. Skipping player initialization."
      );
    }

    return () => {
      try {
        if (playerRef.current) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
      } catch (error) {
        console.warn("Error disposing video player:", error);
      }
      window.removeEventListener("resize", updateDimensions); // Clean up dimensions listener
    };
  }, [videoLoaded, videoContent?.videoContentUrl, annotationsLoaded]);

  useEffect(() => {
    let intervalId = null;

    if (playerRef.current) {
      const player = playerRef.current;
      const checkInterval = 1; // Time interval in milliseconds
      const stopAtAnnotation = () => {

        // Safe currentTime retrieval with null checks
        let currentTime = 0;
        try {
          if (player && typeof player.currentTime === 'function') {
            currentTime = player.currentTime();
          } else {
            // Player is not ready or has been disposed
            return;
          }
        } catch (error) {
          console.warn("⚠️ Error getting current time in stopAtAnnotation:", error);
          return;
        }
        const frameRates = frameRate || 30; // Default to 30 FPS
        const frameDuration = 1 / frameRates; // Time per frame
        const tolerance = frameDuration; // Use one frame duration for better detection

        // Create a new array to hold updated annotations
        let updatedAnnotations = [...annotations];
        let needsUpdate = false;

        // Reset processed annotations if video is rewound
        updatedAnnotations = updatedAnnotations.map((annotation) => {
          if (
            annotation.processed &&
            currentTime < annotation.timeRange.startTime - 0.5 // BUFFER: Only reset if rewound more than 0.5s before annotation
          ) {
            needsUpdate = true;
            return { ...annotation, processed: false };
          }
          return annotation;
        });

        // Find the annotation that matches the current time range with frame-accurate precision
        const matchingAnnotation = updatedAnnotations.find(
          (annotation) => {
            if (!annotation?.timeRange?.startTime || annotation.processed) {
              return false;
            }
            // Check if we've reached or passed the annotation time
            return currentTime >= annotation.timeRange.startTime &&
              currentTime < annotation.timeRange.startTime + 0.05; // Small buffer for frame accuracy
          }
        );
        //Before tolerance value was 4 which 1/4th of a second i.e 0.25s
        //Now tolerance value is 5 which 1/20th of a second i.e 0.05s and it is added for better stopping accuracy

        // Debug logging
        if (updatedAnnotations.length > 0) {
          updatedAnnotations.forEach((annotation, index) => {
            if (annotation?.timeRange?.startTime) {
              const diff = Math.abs(annotation.timeRange.startTime - currentTime);

            }
          });
        }

        if (matchingAnnotation) {
          // Ignore annotations at or near t=0 to allow clean start
          if (
            matchingAnnotation?.timeRange?.startTime !== undefined &&
            matchingAnnotation.timeRange.startTime <= 0.05 &&
            currentTime <= 0.1
          ) {
            return;
          }
          try {

            // First seek to the exact annotation time
            if (player && typeof player.currentTime === 'function') {
              player.currentTime(matchingAnnotation.timeRange.startTime);
              // Small timeout to ensure seek is complete before pausing
              setTimeout(() => {
                if (player && typeof player.pause === 'function') {
                  player.pause();
                }
              }, 50);

            }

            // Update the matching annotation's processed status
            updatedAnnotations = updatedAnnotations.map((annotation) => {
              if (annotation === matchingAnnotation) {
                needsUpdate = true;
                return { ...annotation, processed: true };
              }
              return annotation;
            });


            if (!isPaused) {
              // If the player is playing, seek to the annotation time
              if (player && typeof player.currentTime === 'function') {
                player.currentTime(matchingAnnotation.timeRange.startTime); // Seek accurately
              }
            }

          } catch (error) {
            console.warn("⚠️ Error in annotation processing:", error);
          }
        }

        // Update annotations state if changes were made
        if (needsUpdate) {
          setAnnotations(updatedAnnotations);

          // If in standalone mode, update the Redux store
          if (env === "standalone") {
            const standAloneFeedback = {
              _id: videoId,
              feedback: updatedAnnotations,
            };
            dispatch(addStandaloneFeedback(standAloneFeedback));
          }
        }
      };

      // Set an interval to check every 16 milliseconds (~60fps) to save resources
      intervalId = setInterval(stopAtAnnotation, 16);
    }

    // Cleanup the interval when the component unmounts or dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [annotations, frameRate, isPaused, env, videoId]);

  useEffect(() => {
    const fetchAnnotations = async () => {
      if (!getCoachFeedback) return;
      try {
        const response = await getCoachFeedback(jwtToken, videoId);
        const data = response.data.feedbacks;


        if (!Array.isArray(data)) {
          throw new Error("Invalid data format received from the server.");
        }

        setAnnotations(data);
        setAnnotationsLoaded(true);
        if (setDisplayDialogBoxForNewAIFeedbacks && typeof setDisplayDialogBoxForNewAIFeedbacks === 'function') {
          setDisplayDialogBoxForNewAIFeedbacks(response?.data?.areNewAiFeedbacksPresent);
        }
      } catch (error) {
        console.error("Error fetching annotations:", error.message);
        toast.error(
          error.message ||
          "An unexpected error occurred while fetching annotations."
        );
        setAnnotationsLoaded(true); // Still set to true so video can load
      }
    };

    const initializeStandaloneData = () => {
      const standaloneVideoFeedback = standAloneFeedbacks.find(
        (feedback) => feedback._id === videoId
      );
      const feedbackData = standaloneVideoFeedback
        ? [...standaloneVideoFeedback.feedback]
        : [];
      setAnnotations(feedbackData);
      setAnnotationsLoaded(true);
    };

    // Always fetch annotations first, then load video
    if (env === "standalone") {
      initializeStandaloneData();
    } else {
      fetchAnnotations();
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [env, jwtToken, videoId]);

  useEffect(() => {
    if (env === "standalone" && videoId) {
      const standaloneVideoFeedback = standAloneFeedbacks.find(
        (feedback) => feedback._id === videoId
      );
      const feedbackData = standaloneVideoFeedback
        ? [...standaloneVideoFeedback.feedback]
        : [];
      setAnnotations(feedbackData);
    }
  }, [env, videoId, standAloneFeedbacks]);

  useEffect(() => {
    const updateDimensions = () => {
      if (videoRef.current) {
        const video = videoRef.current;
        const containerWidth = video.offsetWidth;
        const containerHeight = video.offsetHeight;

        const videoAspectRatio = video.videoWidth / video.videoHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let displayWidth, displayHeight;
        let top = 0;
        let left = 0;

        if (containerAspectRatio > videoAspectRatio) {
          // Container is wider than the video aspect ratio
          displayHeight = containerHeight;
          displayWidth = videoAspectRatio * displayHeight;

          // Center the video horizontally
          left = (containerWidth - displayWidth) / 2;
        } else {
          // Container is taller than the video aspect ratio
          displayWidth = containerWidth;
          displayHeight = displayWidth / videoAspectRatio;

          // Center the video vertically
          top = (containerHeight - displayHeight) / 2;
        }

        setVideoDimensions && setVideoDimensions({
          width: displayWidth,
          height: displayHeight,
          top: top,
          left: left,
        });
      }
    };

    const handleLoadedMetadata = () => {
      updateDimensions();
    };

    const currentVideoRef = videoRef.current;

    if (currentVideoRef) {
      currentVideoRef.addEventListener("loadedmetadata", handleLoadedMetadata);
    }

    return () => {
      if (currentVideoRef) {
        currentVideoRef.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
      }
    };
  }, []);

  // Extract screenshot logic
  function handleScreenshot(player) {
    const videoEl = player.el().querySelector('video');
    if (!videoEl) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoEl.videoWidth || 640;
    canvas.height = videoEl.videoHeight || 360;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    const image = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = image;
    link.download = `screenshot-${Date.now()}.png`;
    link.click();
  }

  // ModesButton logic removed

  // Add keyboard shortcuts help
  const showKeyboardShortcuts = () => {
    const shortcuts = [
      'Spacebar: Play/Pause',
      '← → Arrow Keys: Seek 5 seconds',
      '↑ ↓ Arrow Keys: Volume up/down',
      'Page Up/Down: Seek 10 seconds',
      'Home/End: Seek to beginning/end',
      '0-9 Keys: Seek to percentage (10%-90%)',
      'Ctrl+F: Fullscreen toggle',
      'Ctrl+P: Picture-in-Picture',
      'Ctrl+S: Take screenshot',
      'Ctrl+M: Modes button',
      'F1 or Ctrl+H: Show this help'
    ];

    const message = '🎮 Keyboard Shortcuts:\n\n' + shortcuts.join('\n');
    alert(message);
  };

  // Add keyboard shortcuts help button
  if (!videojs.getComponent('KeyboardHelpButton')) {
    const Button = videojs.getComponent('Button');
    class KeyboardHelpButton extends Button {
      constructor(player, options) {
        super(player, options);
        this.controlText("Keyboard Shortcuts");
        this.addClass('vjs-keyboard-help');
        this.el().innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="4" width="16" height="12" rx="2" stroke="white" stroke-width="2" fill="none"/>
            <rect x="4" y="6" width="2" height="2" fill="white"/>
            <rect x="7" y="6" width="2" height="2" fill="white"/>
            <rect x="10" y="6" width="2" height="2" fill="white"/>
            <rect x="13" y="6" width="2" height="2" fill="white"/>
            <rect x="4" y="9" width="2" height="2" fill="white"/>
            <rect x="7" y="9" width="2" height="2" fill="white"/>
            <rect x="10" y="9" width="2" height="2" fill="white"/>
            <rect x="13" y="9" width="2" height="2" fill="white"/>
            <rect x="4" y="12" width="2" height="2" fill="white"/>
            <rect x="7" y="12" width="2" height="2" fill="white"/>
            <rect x="10" y="12" width="2" height="2" fill="white"/>
            <rect x="13" y="12" width="2" height="2" fill="white"/>
          </svg>
        `;
      }
      handleClick() {
        showKeyboardShortcuts();
      }
    }
    videojs.registerComponent('KeyboardHelpButton', KeyboardHelpButton);
  }

  // Ref to always have the latest player instance
  const latestPlayerRef = useRef();
  useEffect(() => {
    latestPlayerRef.current = playerRef.current;
  }, [playerRef.current]);

  useEffect(() => {
    // Keyboard shortcuts integration (robust global listener)
    const handleKeyDown = (e) => {
      const player = latestPlayerRef.current;
      const playerEl = player && player.el && player.el();
      if (!playerEl) return;

      // Don't handle keyboard events when typing in input fields
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      switch (e.key) {
        case "ArrowRight": // Forward seek
          if (player) {
            player.currentTime(Math.min(player.duration(), player.currentTime() + 5));
          }
          break;

        case "ArrowLeft": // Backward seek
          if (player) {
            player.currentTime(Math.max(0, player.currentTime() - 5));
          }
          break;

        case " ":
        case "Spacebar": // For older browsers
          if (e.code === "Space" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation(); // Prevent bubbling to other listeners
            if (player.paused()) {
              player.play();
            } else {
              player.pause();
            }
          }
          break;

        case "f":
        case "F": // Fullscreen with Ctrl + F (Cmd + F on Mac)
          if ((e.ctrlKey || e.metaKey) && player) {
            if (!player.isFullscreen()) {
              player.requestFullscreen();
            } else {
              player.exitFullscreen();
            }
            e.preventDefault(); // Prevent browser Find action
          }
          break;

        case "p":
        case "P": // Picture-in-Picture with Ctrl + P (Cmd + P on Mac)
          if ((e.ctrlKey || e.metaKey) && player) {
            const videoEl = player.el().querySelector("video");
            if (videoEl && document.pictureInPictureEnabled) {
              if (document.pictureInPictureElement) {
                document.exitPictureInPicture();
              } else {
                videoEl.requestPictureInPicture();
              }
            }
            e.preventDefault(); // Prevent browser Print action
          }
          break;

        case "s":
        case "S": // Screenshot with Ctrl + S
          if (e.ctrlKey && player) {
            e.preventDefault();
            handleScreenshot(player);
          }
          break;

        case "m":
        case "M": // Modes button keyboard shortcut removed
          break;

        case "ArrowUp": // Volume up
          if (player) {
            e.preventDefault();
            const currentVolume = player.volume();
            const newVolume = Math.min(1, currentVolume + 0.1);
            player.volume(newVolume);
            console.log("Volume up:", newVolume);
          }
          break;

        case "ArrowDown": // Volume down
          if (player) {
            e.preventDefault();
            const currentVolume = player.volume();
            const newVolume = Math.max(0, currentVolume - 0.1);
            player.volume(newVolume);
            console.log("Volume down:", newVolume);
          }
          break;

        case "0": // Seek to beginning
          if (player) {
            e.preventDefault();
            player.currentTime(0);
            console.log("Seeking to beginning");
          }
          break;

        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
        case "6":
        case "7":
        case "8":
        case "9": // Seek to percentage (10% to 90%)
          if (player) {
            e.preventDefault();
            const percentage = parseInt(e.key) * 10;
            const seekTime = (player.duration() * percentage) / 100;
            player.currentTime(seekTime);
            console.log(`Seeking to ${percentage}%`);
          }
          break;

        case "Home": // Seek to beginning
          if (player) {
            e.preventDefault();
            player.currentTime(0);
            console.log("Seeking to beginning (Home)");
          }
          break;

        case "End": // Seek to end
          if (player) {
            e.preventDefault();
            player.currentTime(player.duration());
            console.log("Seeking to end (End)");
          }
          break;

        case "PageUp": // Seek forward 10 seconds
          if (player) {
            e.preventDefault();
            player.currentTime(Math.min(player.duration(), player.currentTime() + 10));
            console.log("Seeking forward 10 seconds");
          }
          break;

        case "PageDown": // Seek backward 10 seconds
          if (player) {
            e.preventDefault();
            player.currentTime(Math.max(0, player.currentTime() - 10));
            console.log("Seeking backward 10 seconds");
          }
          break;

        case "F1": // Show keyboard shortcuts help
        case "h":
        case "H": // Show keyboard shortcuts help with Ctrl+H
          if (e.key === "F1" || (e.ctrlKey && (e.key === "h" || e.key === "H"))) {
            e.preventDefault();
            showKeyboardShortcuts();
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  // Always declare annotation-related state (hooks must not be conditional)
  const [videoDimensions, setVideoDimensions] = React.useState({ width: 0, height: 0, top: 0, left: 0 });
  const [newAnnotation, setNewAnnotation] = React.useState(null);
  const [drawing, setDrawing] = React.useState(false);

  // Ensure player is always initialized when video is ready (player side or coach side)
  useEffect(() => {
    if (
      videoLoaded &&
      videoRef.current &&
      !playerRef.current &&
      annotationsLoaded
    ) {
      loadVideo();
    }
  }, [videoLoaded, videoRef, playerRef, loadVideo, annotationsLoaded]);

  // useEffect(() => {
  //   if (
  //     videoLoaded &&
  //     videoRef.current &&
  //     !playerRef.current &&
  //     annotations !== undefined
  //   ) {
  //     loadVideo();
  //   }
  // }, [videoLoaded, videoRef, playerRef, loadVideo, annotations]);

  // Duplicate ModesButton registration removed from here as it was already registered at the top level

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden', backgroundColor: 'black' }}>
      {!videoLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-40 bg-black">
          <div className="w-10 h-10 border-4 border-white/20 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
      )}

      <div data-vjs-player style={{ width: '100%', height: '100%' }}>
        <video
          key={videoKey}
          ref={videoRef}
          onPause={handlePauseVideo}
          onPlay={handlePlayVideo}
          onEnded={handleVideoEnd}
          className="video-js vjs-big-play-centered"
          style={{ width: '100%', height: '100%' }}
          playsInline
          crossOrigin="anonymous"
          poster={thumbnailUrl}
        />
      </div>

      {showAnnotations && (
        <AnnotationStage
          videoRef={videoRef}
          annotations={annotations}
          newAnnotation={newAnnotation}
          setNewAnnotation={setNewAnnotation}
          drawing={drawing}
          setDrawing={setDrawing}
          playerRef={playerRef}
          isPaused={isPaused}
          key={`anno-${videoKey}`}
          isPlayerSide={isPlayerSide}
        />
      )}
    </div>
  );
};

export default VideoPlayer;
